import { defineComponent as _defineComponent } from 'vue'
import { store } from '@/store';
import QueryParamService, { QueryParams } from '@/core/query-params.service';
import { PurchaseCtaEnum } from '@/services/product.service';
import ScrollService from '@/core/scroll.service';
import PDPSwiper from './pdp-swiper/PDPSwiper.vue';
import Dropdown from '@/ui/shared/Dropdown.vue';
import ProductVariantSpec from './ProductVariantSpec.vue';
import ProductPropertySelector from './ProductPropertySelector.vue';
import { TabEnum } from '@/ui/shared/Tabs.vue';
import DomService, { BodyDomClasses } from '@/core/dom.service';
import { ProductService } from '@/services';
import { initializeProfessionalFilter, ProductProperty, ProfessionalPrioritizedVariantFilterObject, ProductPropertyData, ProductPropertyOption } from './product-properties';
import { uniqBy } from 'lodash';
import useBreakpoints from '@/core/responsive/breakpoints/breakpoint.mixin';
import ProductViewInAugmentedReality from '@/ui/views/product-details/ProductViewInAugmentedReality.vue';
import GetCutsheetLink from '@/ui/views/product-details/get-cutsheet/GetCutsheetLink.vue';

import { computed, onBeforeUnmount, ref, watch } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductVariantPickerProfessional',
  props: {
    hiddenQuoteProperties: { default: () => [] },
    isConfigurable: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as { hiddenQuoteProperties: string[], isConfigurable?: boolean }

const breakpointIsActive = useBreakpoints();



const product = computed<ProfessionalProductViewModel>(() => store.getters['catalog/product']);
const selectedVariant = computed<PrivateVariantViewObject>(() => store.getters['catalog/selectedVariant']);
const productPropertySelectorVariants = computed<ProfessionalVariantViewObject[]>(() => store.getters['catalog/productPropertySelectorVariants']);
const validProductPropertySelectorTypes = computed<ProductPropertySelectorType[]>(() => store.getters['catalog/validProductPropertySelectorTypes']);
const hasChangedRoute = computed<boolean>(() => store.getters['app/hasChangedRoute']);
const isB2BAuthenticated = computed<boolean>(() => store.getters['app/isB2BAuthenticated']);
const selectedLanguage = computed<MarketViewObject>(() => store.getters['app/selectedLanguage']);
const onUsMarket = computed<boolean>(() => (selectedLanguage.value.code === 'en-US'));

function selectVariant(variant: ProfessionalVariantViewObject) {
    store.dispatch('catalog/selectVariant', variant);
}

const loadingSapInfo = ref<boolean>(true);
const sapVariantInfo = ref<Vertica.LouisPoulsen.Application.Commerce.ViewModels.SapVariantViewModel | null>(null);

// important - the sequence matters as the filters are applied by priority
const prioritizedVariantFilter = ref<ProfessionalPrioritizedVariantFilterObject>(initializeProfessionalFilter());

const colors = ref<IColorOption[]>([]);
const options = ref<ProductPropertyData[]>([]);
const defaultImages = ref< { [colorCode: string]: CarouselImageViewObject[] }>();

const configuredVariant = ref<ProfessionalVariantViewObject>(null);

const hasTrackedVariant = ref<boolean>(false);

const hasSplashText = computed<boolean>(() => {
    return configuredVariant.value && (configuredVariant.value.isInQuickShip || !!configuredVariant.value.splashText1 || !!configuredVariant.value.splashText2);
});

function initialize() {
    configuredVariant.value = getVariantFromUrl() || getDefaultVariant();
    if (configuredVariant.value) {
        selectVariant(configuredVariant.value);
    }

    colors.value = getDistinctColors();
    defaultImages.value = uniqBy(product.value.variants, v => v.colorCode).reduce((prev, item) => {
        prev[item.colorCode] = item.images.slice(0, 1);
        return prev;
    }, {});

    // important - the sequence matters as the filters are applied by priority
    options.value = [
        {
            property: ProductProperty.propertyNameSizeText,
            options: getOptionsFor(ProductProperty.propertyNameSizeText)
        },
        {
            property: ProductProperty.propertyNameArmText,
            options: getOptionsFor(ProductProperty.propertyNameArmText)
        },
        {
            property: ProductProperty.propertyNameMountingText,
            options: getOptionsFor(ProductProperty.propertyNameMountingText)
        },
        {
            property: ProductProperty.propertyNameAdditionalFeatures,
            options: getOptionsFor(ProductProperty.propertyNameAdditionalFeatures, ProductProperty.propertyNameAdditionalFeaturesDescription)
        },
        {
            property: ProductProperty.propertyNameShieldDiffusor,
            options: getOptionsFor(ProductProperty.propertyNameShieldDiffusor)
        },
        {
            property: ProductProperty.propertyNameBody,
            options: getOptionsFor(ProductProperty.propertyNameBody)
        }
    ];
    DomService.addElementClass('body', BodyDomClasses.pdpActive);
}

initialize();

// This needs to be changed to a better solution.
// LP wants this fix until they have decided how it should be done.
const showPrice = computed(() => {
    if (product.value.isDecorative) {
        return true;
    }

    return false;
});

onBeforeUnmount(() => {
    DomService.removeElementClass('body', BodyDomClasses.pdpActive);
});

watch(() => selectedVariant.value, onSelectedVariantChanged, { immediate: true });
function onSelectedVariantChanged(variant: VariantBaseViewObject) {
    prioritizedVariantFilter.value.sizeText.selectedValue = variant.sizeText;
    prioritizedVariantFilter.value.colorCode.selectedValue = variant.colorCode;
    prioritizedVariantFilter.value.armText.selectedValue = variant.armText;
    prioritizedVariantFilter.value.mountingText.selectedValue = variant.mountingText;
    prioritizedVariantFilter.value.additionalFeatures.selectedValue = variant.additionalFeatures;
    prioritizedVariantFilter.value.shieldDiffusor.selectedValue = variant.shieldDiffusor;
    prioritizedVariantFilter.value.body.selectedValue = variant.body;

    trackVariant();
    getSapVariantInfo();
}

function ctaIs(cta: PurchaseCtaEnum): boolean {
    return configuredVariant.value.purchaseCta === cta as any;
}

const selectedOptionsForQuote = computed<{ [key: string]: string }>(() => {
    const options = {};
    for (const key of Object.keys(prioritizedVariantFilter.value)) {
        const code = prioritizedVariantFilter.value[key].selectedValue;
        let text: any;
        if (key === ProductProperty.propertyNameColorCode) {
            const color = colors.value.find(option => option.code === code);
            text = color ? color.text : code;
        } else { text = code; }
        const optionKey = key === ProductProperty.propertyNameColorCode ? 'multiColor' : key;
        options[optionKey] = text;
    }
    return options;
});

function getDistinctColors() {
    return uniqBy(productPropertySelectorVariants.value, v => v.colorCode).map(v => ({
        code: v.colorCode,
        image: v.colorImage,
        text: v.colorText
    }));
}

function getOptionsFor(property: ProductPropertySelectorType, descriptionProperty: string = null): ProductPropertyOption[] {
    const allPropertyOptions = uniqBy(productPropertySelectorVariants.value.filter(v => v[property] !== null), property);
    const propertiesForType = allPropertyOptions.map(x => ({
        value: x[property],
        description: descriptionProperty ? x[descriptionProperty] : null
    }));
    return propertiesForType;
}

function getVariantIdFromUrl() : string {
    const id = QueryParamService.getParam(QueryParams.SelectedVariantId);
    return id;
}

function getVariantFromUrl() {
    // variant from url will always overrule variant.isDefault
    const id = getVariantIdFromUrl();
    return productPropertySelectorVariants.value.find(x => x.id === id);
}

function getDefaultVariant() {
    const defaultVariant = productPropertySelectorVariants.value.find(v => v.isDefault);
    return defaultVariant || productPropertySelectorVariants.value[0];
}

function findBestVariantCandidate(lastPropertyChanged: string, propertyValue: string) {
    const matchValue = (value: string, selected: string) => {
        const isMatchingEmpty = value === null && (selected === (Dropdown as any).EMPTY_VALUE || selected === '');
        const isMatchingValue = value === selected;
        return isMatchingValue || isMatchingEmpty;
    };

    let candidates = productPropertySelectorVariants.value.filter(v => matchValue(v[lastPropertyChanged], propertyValue));
    Object.keys(prioritizedVariantFilter.value).forEach(propertyName => {
        const entry = prioritizedVariantFilter.value[propertyName];
        if (entry.selectedValue) {
            const filteredCandidates = candidates.filter(v => matchValue(v[propertyName], entry.selectedValue));
            if (filteredCandidates.length > 0) {
                candidates = filteredCandidates; // Update the original list only if filteredCandidates contain something
            }
        }
    });

    configuredVariant.value = candidates.length > 0 ? candidates[0] : null;
    if (configuredVariant.value) {
        selectVariant(configuredVariant.value);
    }
}

function changeFilter(value: string, property: string) {
    prioritizedVariantFilter.value[property].selectedValue = value;
    findBestVariantCandidate(property, value);
}

function goToConfigurator() {
    const elementId = props.isConfigurable ? TabEnum.ProductConfigurator : TabEnum.Configurator;
    QueryParamService.setQueryParamValue(QueryParams.AccordionState, elementId);
    setTimeout(() => {
        const elementToScrollTo = document.getElementById(elementId);
        ScrollService.scrollToElement(elementToScrollTo, 0, 300);
    }, 300);
}

function trackVariant() {
    if (configuredVariant.value) {
        const useFirstPdpEventName = !hasChangedRoute.value && !hasTrackedVariant.value;

        if ((window as any).dataLayer) {
            const productForDataLayer = {
                id: configuredVariant.value.sapId,
                price: '', // Professional products has no track-able price at this point,
                dimension3: getVariantIdFromUrl()
            };
            (window as any).dataLayer.push({
                event: useFirstPdpEventName ? 'firstpdp' : 'pdp',
                ecommerce: {
                    currencyCode: product.value.productTracking.currencyCode ? product.value.productTracking.currencyCode : '',
                    detail: {
                        products: [productForDataLayer]
                    }
                }
            });
            hasTrackedVariant.value = true;
        }
    }
}

const images = computed(() => {
    if (configuredVariant.value) {
        return configuredVariant.value.images;
    }
    return defaultImages.value[prioritizedVariantFilter.value.colorCode.selectedValue];
});

const configuredVariantImageTitle = computed<string>(() => {
    if (configuredVariant.value) {
        const titleElements = [product.value.productName];
        validProductPropertySelectorTypes.value.forEach((type:string) => {
            if (type === 'colorCode') {
                type = 'colorText';
            }
            const configuredVariantTypeValue = configuredVariant.value[type];
            if (configuredVariantTypeValue && configuredVariantTypeValue !== '-') {
                titleElements.push(configuredVariantTypeValue);
            }
        });
        return titleElements.join(' - ');
    }
    return '';
});

function getSapVariantInfo(): void {
    if (isB2BAuthenticated.value && configuredVariant.value) {
        loadingSapInfo.value = true;
        ProductService.getSapVariant(configuredVariant.value.sapId).then((info) => {
            sapVariantInfo.value = info;
            loadingSapInfo.value = false;
        }).catch(() => {
            loadingSapInfo.value = false;
        });
    }
}

function openSignUpLinkInNewTab() {
    window.open(product.value.signUpForTradeLinkUrl, '_blank');
}

return { __sfc: true,breakpointIsActive, props, product, selectedVariant, productPropertySelectorVariants, validProductPropertySelectorTypes, hasChangedRoute, isB2BAuthenticated, selectedLanguage, onUsMarket, selectVariant, loadingSapInfo, sapVariantInfo, prioritizedVariantFilter, colors, options, defaultImages, configuredVariant, hasTrackedVariant, hasSplashText, initialize, showPrice, onSelectedVariantChanged, ctaIs, selectedOptionsForQuote, getDistinctColors, getOptionsFor, getVariantIdFromUrl, getVariantFromUrl, getDefaultVariant, findBestVariantCandidate, changeFilter, goToConfigurator, trackVariant, images, configuredVariantImageTitle, getSapVariantInfo, openSignUpLinkInNewTab, PurchaseCtaEnum, PDPSwiper, ProductVariantSpec, ProductPropertySelector, ProductViewInAugmentedReality, GetCutsheetLink }
}

})